import { supabase } from "@/integrations/supabase/client";

/**
 * Sending a fresh confirmation link from wherever she is standing. Issue #1532.
 *
 * ── Why this is a module and not two copies of five lines ───────────────────
 *
 * Three surfaces need it now: the expired-link landing (AuthCallback), the
 * sign-in screen once #1531 tells her the account is unconfirmed, and the
 * "Check your email" screen SignUp already had. They must agree about the
 * cooldown, the copy, and — the part that is easy to get wrong — about reading
 * the result before claiming anything happened.
 *
 * ⚠️ `supabase.auth.resend` RESOLVES `{ error }`; it does not throw. A
 * `try/catch` around it is decorative (CLAUDE.md guardrail 16). The only
 * correct shape is `const { error } = await …` and then branching on it, which
 * is what this function does so no caller has to remember to.
 *
 * ── The cooldown is real and it is short ────────────────────────────────────
 *
 * Measured against this project's GoTrue on Sep 9 2026:
 *
 *   {"error_code":"over_email_send_rate_limit",
 *    "msg":"For security purposes, you can only request this after 47 seconds."}
 *
 * The natural reaction to "nothing arrived yet" is to tap again, so a button
 * that re-enables immediately hands her a failure that reads as a broken
 * feature. Callers disable for RESEND_COOLDOWN_SECONDS after a SUCCESSFUL send,
 * and show RESEND_COOLDOWN_MESSAGE if GoTrue rate-limits anyway (her clock and
 * ours can disagree — an earlier send from another device still counts).
 */

/**
 * Slightly longer than the ~47s GoTrue quoted, so the button comes back after
 * the window has actually closed rather than one second inside it.
 */
export const RESEND_COOLDOWN_SECONDS = 60;

export const RESEND_SENT_MESSAGE = "Fresh link sent 💜 Check your inbox.";

/** Same wording SmsSetupSheet already uses for the same situation. */
export const RESEND_COOLDOWN_MESSAGE = "Give it a minute and tap Resend again 💜";

export const RESEND_FAILED_MESSAGE =
  "Couldn't send that link right now. Try again in a minute?";

/** GoTrue's confirmation-email rate limit, by code with a wording fallback. */
export function isEmailRateLimitError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { code, status, message } = error as {
    code?: unknown;
    status?: unknown;
    message?: unknown;
  };

  if (code === "over_email_send_rate_limit") return true;
  if (status === 429) return true;

  return (
    typeof message === "string" &&
    /rate limit|only request this after|too many/i.test(message)
  );
}

export interface ResendOutcome {
  ok: boolean;
  /** What the caller should show her. Already decided here, so no surface
   *  invents its own wording or renders a raw GoTrue string. */
  message: string;
  /** Whether the button should go quiet for RESEND_COOLDOWN_SECONDS. True after
   *  a send that reached GoTrue, and true when GoTrue rate-limited us — that
   *  means a send is already in flight, so tapping again is just as useless.
   *  Deciding it here keeps the two call sites from disagreeing. */
  cooldown: boolean;
}

/**
 * Ask GoTrue for a new signup confirmation link.
 *
 * Returns what the caller should SAY, already decided, so no surface invents
 * its own wording or — worse — confirms over an error it never read.
 */
export async function resendConfirmationEmail(email: string): Promise<ResendOutcome> {
  const { error } = await supabase.auth.resend({
    type: "signup",
    email: email.trim(),
    // Same landing as SignUp.tsx. A fresh link must come back to the route that
    // knows how to read both #626 link shapes.
    options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
  });

  if (error) {
    if (isEmailRateLimitError(error)) {
      return { ok: false, message: RESEND_COOLDOWN_MESSAGE, cooldown: true };
    }
    // Never render error.message: it leaks our Supabase hostname, which is the
    // whole reason lib/authNetworkError.ts exists.
    return { ok: false, message: RESEND_FAILED_MESSAGE, cooldown: false };
  }

  return { ok: true, message: RESEND_SENT_MESSAGE, cooldown: true };
}
